import { useEffect, useMemo } from 'react';
import {
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  useMaybeTrackRefContext,
  useParticipants,
  useRoomContext,
  useTracks,
} from '@livekit/components-react';
import { Track, type Room } from 'livekit-client';

import type { EffectiveClass } from '../lib/effectiveClass';
import { displayForParticipant, type SessionParticipantNames } from '../lib/participantDisplayName';
import { useBotLeftMidCall } from '../hooks/useBotLeftMidCall';
import { useCallKeepAlive } from '../hooks/useCallKeepAlive';
import { useHumanParticipants } from '../hooks/useHumanParticipants';
import { useIsRecording } from '../hooks/useIsRecording';
import { useSessionTranscript } from '../hooks/useSessionTranscript';
import { useSavedCallTranscript } from '../hooks/useSavedCallTranscript';
import { useAssistantSession } from '../hooks/useAssistantSession';
import { isAgentParticipant } from '../lib/isAgentParticipant';
import { mergeSavedTranscript } from '../lib/mergeSavedTranscript';
import type { MuteParticipantInput, KickParticipantInput } from '../services/videoCallService';

import { AudioParticipantList } from './AudioParticipantList';
import { CallControlBar, type SidePanelId } from './CallControlBar';
import { CallStatusChip } from './CallStatusChip';
import { LocalParticipantPip } from './LocalParticipantPip';
import { ParticipantNameplate } from './ParticipantNameplate';
import { ParticipantsBody } from './ParticipantsBody';
import { SidePanel } from './SidePanel';
import { StatusLine } from './StatusLine';
import { TranscriptBody } from './TranscriptBody';
import { TranscriptionActiveBadge } from './TranscriptionActiveBadge';
import { AssistantPanel } from './assistant/AssistantPanel';

interface VideoCallStageProps {
  effectiveClass: EffectiveClass;
  mode: 'online' | 'helpline';
  audioOnly?: boolean;
  isPaused: boolean;
  openPanel: SidePanelId | null;
  isRecordingPending: boolean;
  sessionNames: SessionParticipantNames;
  onLeave: () => void;
  onLeaveAndComplete?: () => void;
  onMarkNoShow?: () => void;
  onRoomReady?: (room: Room) => void;
  onStartRecording?: () => void;
  onStopRecording?: () => void;
  onTogglePanel: (id: SidePanelId) => void;
  onMuteParticipant?: (input: MuteParticipantInput) => void;
  onKickParticipant?: (input: KickParticipantInput) => void;
  sessionId: string;
  /** `session.actualStart` (ISO) — anchors the CallStatusChip timer. Null
   *  until the LiveKit `room_started` webhook stamps it on first join. */
  actualStart: string | null;
  clientUserId: string | null;
  practitionerUserId: string | null;
}

export function VideoCallStage({
  effectiveClass,
  mode,
  audioOnly = false,
  isPaused,
  openPanel,
  isRecordingPending,
  sessionNames,
  onLeave,
  onLeaveAndComplete,
  onMarkNoShow,
  onRoomReady,
  onStartRecording,
  onStopRecording,
  onTogglePanel,
  onMuteParticipant,
  onKickParticipant,
  sessionId,
  actualStart,
  clientUserId,
  practitionerUserId,
}: VideoCallStageProps) {
  const room = useRoomContext();

  // Being on a call is presence, even with no mouse or keyboard input for the
  // whole hour. Without this the idle lockout fired at the end of a
  // 60-minute session. Lives here because it is the one component both the
  // staff and client call routes render inside <LiveKitRoom>.
  useCallKeepAlive();

  useEffect(() => {
    if (!onRoomReady) return;
    onRoomReady(room);
  }, [room, onRoomReady]);

  const participants = useHumanParticipants();
  const remoteCount = participants.filter((p) => !p.isLocal).length;

  const allParticipants = useParticipants();
  const botPresent = allParticipants.some((p) => isAgentParticipant(p));

  const botLeftMidCall = useBotLeftMidCall(botPresent);

  const { segments: liveSegments } = useSessionTranscript(sessionId);
  // The live feed only hears lines spoken after this tab joined, so after a
  // rejoin or reload staff also load the lines the agent already saved.
  // Clients get no transcript panel at all for now (product call, 2026-10-15).
  const showTranscript = effectiveClass === 'practitioner' || effectiveClass === 'observer';
  const savedSegments = useSavedCallTranscript(showTranscript ? sessionId : null);
  const segments = useMemo(
    () =>
      mergeSavedTranscript({
        live: liveSegments,
        saved: savedSegments,
        clientUserId,
        practitionerUserId,
      }),
    [liveSegments, savedSegments, clientUserId, practitionerUserId],
  );
  // Source of truth for recording state — LiveKit broadcasts
  // RecordingStatusChanged to every participant when egress starts/stops,
  // so practitioner + client tabs all reflect the same value. Earlier the
  // flag was practitioner-local React state that the client tab couldn't
  // see at all, which is why "recording" was invisible on the client side.
  const isRecording = useIsRecording();

  const showRecordingControl = effectiveClass === 'practitioner';
  const showAssistantToggle = effectiveClass === 'practitioner';
  const showParticipantsToggle = effectiveClass === 'practitioner' || effectiveClass === 'observer';

  const assistantSession = useAssistantSession(sessionId, { enabled: showAssistantToggle });

  // Lazy-connect: on first time the assistant panel opens, the hook mints
  // a ticket and opens the WS. The hook stays mounted for the entire call
  // so closing the panel doesn't tear down the connection.
  const { ensureConnected: ensureAssistantConnected } = assistantSession;
  useEffect(() => {
    if (openPanel === 'assistant') ensureAssistantConnected();
  }, [openPanel, ensureAssistantConnected]);

  const localMicMuted = useLocalMicMuted();
  const local = room.localParticipant;
  const localDisplay = displayForParticipant(
    { identity: local.identity, name: local.name, isLocal: true },
    effectiveClass,
    sessionNames,
  );

  const transcriptSubtitle = botLeftMidCall ? (
    <StatusLine tone="warn">Transcription paused — audio still recording</StatusLine>
  ) : botPresent ? (
    <StatusLine tone="active">Transcribing in real time</StatusLine>
  ) : (
    <StatusLine tone="idle">Waiting for the transcription service</StatusLine>
  );

  const assistantSubtitle = (() => {
    switch (assistantSession.status) {
      case 'idle':
      case 'connecting':
        return <StatusLine tone="idle">Connecting…</StatusLine>;
      case 'ready':
        return <StatusLine tone="active">Ready</StatusLine>;
      case 'asking':
        return <StatusLine tone="active">Thinking…</StatusLine>;
      case 'reconnecting':
        return <StatusLine tone="warn">Reconnecting…</StatusLine>;
      case 'unauthorized':
        return <StatusLine tone="warn">Unavailable for this session</StatusLine>;
    }
  })();

  const participantsSubtitle =
    remoteCount === 0 ? (
      <StatusLine tone="idle">No one else here yet</StatusLine>
    ) : (
      <StatusLine tone="active">{remoteCount} joined</StatusLine>
    );

  return (
    <div
      className="fixed inset-0 flex gap-2 bg-[#080F0E] p-2 text-white sm:gap-3 sm:p-3"
      data-lk-theme="default"
    >
      <main
        className={`@container relative min-w-0 flex-1 overflow-hidden rounded-2xl border border-white/6 bg-[#0B1614] ${
          openPanel ? 'hidden sm:block' : ''
        }`}
      >
        <div className="absolute inset-0">
          {mode === 'online' ? (
            <TileGrid viewerClass={effectiveClass} sessionNames={sessionNames} />
          ) : (
            <AudioParticipantList />
          )}
          <RoomAudioRenderer />
        </div>

        {mode === 'online' ? (
          <LocalParticipantPip
            audioOnly={audioOnly}
            isMicMuted={localMicMuted}
            displayName={localDisplay.name}
            displayRole={localDisplay.role}
          />
        ) : null}

        {/* The REC pill is purely truth-driven now — visible to every
            participant (client + staff) whenever LiveKit egress is
            actually capturing. When recording stops, the pill disappears
            and only the duration timer remains. The transcription badge
            sits below it on the left so it never collides with the
            LocalParticipantPip in the top-right. */}
        <CallStatusChip isPaused={isPaused} showRecording={isRecording} actualStart={actualStart} />
        <TranscriptionActiveBadge botPresent={botPresent} isRecording={isRecording} />

        <CallControlBar
          mode={mode}
          showRecordingControl={showRecordingControl}
          showTranscriptToggle={showTranscript}
          showAssistantToggle={showAssistantToggle}
          showParticipantsToggle={showParticipantsToggle}
          isPaused={isPaused}
          isRecordingPending={isRecordingPending}
          openPanel={openPanel}
          participantCount={remoteCount}
          onLeave={onLeave}
          onLeaveAndComplete={onLeaveAndComplete}
          onMarkNoShow={onMarkNoShow}
          onStartRecording={showRecordingControl ? onStartRecording : undefined}
          onStopRecording={showRecordingControl ? onStopRecording : undefined}
          onTogglePanel={onTogglePanel}
        />
      </main>

      {showTranscript ? (
        <SidePanel
          open={openPanel === 'transcript'}
          onOpenChange={(open) => {
            if (!open) onTogglePanel('transcript');
          }}
          title="Live transcript"
          subtitle={transcriptSubtitle}
        >
          <TranscriptBody
            segments={segments}
            botPresent={botPresent}
            clientUserId={clientUserId}
            practitionerUserId={practitionerUserId}
            viewerClass={effectiveClass}
            sessionNames={sessionNames}
          />
        </SidePanel>
      ) : null}

      {showAssistantToggle ? (
        <SidePanel
          open={openPanel === 'assistant'}
          onOpenChange={(open) => {
            if (!open) onTogglePanel('assistant');
          }}
          title="Ask Elixaa"
          subtitle={assistantSubtitle}
        >
          <AssistantPanel session={assistantSession} />
        </SidePanel>
      ) : null}

      {showParticipantsToggle ? (
        <SidePanel
          open={openPanel === 'participants'}
          onOpenChange={(open) => {
            if (!open) onTogglePanel('participants');
          }}
          title="Participants"
          subtitle={participantsSubtitle}
        >
          <ParticipantsBody onMute={onMuteParticipant} onKick={onKickParticipant} />
        </SidePanel>
      ) : null}
    </div>
  );
}

function TileGrid({
  viewerClass,
  sessionNames,
}: {
  viewerClass: EffectiveClass;
  sessionNames: SessionParticipantNames;
}) {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );
  // Drop AGENT-kind track refs first. The transcription bot is in the
  // LiveKit room as an agent participant; with `withPlaceholder: true`
  // useTracks fabricates a placeholder camera ref for participants that
  // never published a video track — which gives the bot a ghost tile in
  // the grid (empty avatar, fallback nameplate). Filtering at this layer
  // keeps it out of the visible UI without changing the call lifecycle
  // or the transcript pipeline. Mirrors useHumanParticipants (which
  // already does this for the participants list / counters).
  const human = tracks.filter((t) => !isAgentParticipant(t.participant));
  const remote = human.filter((t) => !t.participant.isLocal);
  const list = remote.length > 0 ? remote : human;
  return (
    <GridLayout tracks={list} className="h-full w-full">
      <TileWithNameplate viewerClass={viewerClass} sessionNames={sessionNames} />
    </GridLayout>
  );
}

function TileWithNameplate({
  viewerClass,
  sessionNames,
}: {
  viewerClass: EffectiveClass;
  sessionNames: SessionParticipantNames;
}) {
  const trackRef = useMaybeTrackRefContext();
  if (!trackRef) return <ParticipantTile />;
  const participant = trackRef.participant;
  const display = displayForParticipant(
    { identity: participant.identity, name: participant.name, isLocal: participant.isLocal },
    viewerClass,
    sessionNames,
  );
  const audioPub = participant.getTrackPublication(Track.Source.Microphone);
  const isMuted = Boolean(audioPub?.isMuted);
  return (
    <div className="relative h-full w-full">
      <ParticipantTile trackRef={trackRef} className="h-full w-full" />
      {!participant.isLocal ? (
        <ParticipantNameplate name={display.name} role={display.role} isMuted={isMuted} />
      ) : null}
    </div>
  );
}

function useLocalMicMuted(): boolean {
  const room = useRoomContext();
  const local = room.localParticipant;
  const pub = local.getTrackPublication(Track.Source.Microphone);
  return Boolean(pub?.isMuted) || !pub;
}

export { useLocalMicMuted as _useLocalMicMuted };
